import React from 'react'
import { observer } from 'mobx-react'
import Card from './Card'
import { exhibitorWidgets } from './SalesIntelligenceWidgets'
import AiSummaryWidget from './widgets/AiSummaryWidget'
import CompoundKPI from '../DynamicDashV3/charts/CompoundKPI'
import SatisfactionOfObjectives from './widgets/SatisfactionOfObjectives'
import GroupedVerbatimWidget from './widgets/GroupedVerbatimWidget'
import CollapsibleMatrix from './widgets/CollapsibleMatrix'
import TargetIcon from './widgets/TargetIcon'

const CompoundError = () => (
    <div style={{ color: 'white', fontSize: '16px', fontWeight: '700' }}>
        No data found
    </div>
)

const ExhibitorView = observer(({ style, cssClass, salesIntelligenceStore }) => {
    const { aiSummary, EEI, ECI } = exhibitorWidgets()
    const { companyPageData, error } = salesIntelligenceStore
    const exhibitorRecord = salesIntelligenceStore.companyPageData?.exhibitorList
        ?.find(({ name }) => name === salesIntelligenceStore.company)

    if (error) {
        if (error === 'noObjectivesQuestion') {
            return (
                <div>
                    No Objectives question has been set on the survey.
                    Please contact your survey administrator to rectify this
                </div>
            )
        }
        return (
            <div>
                No data has been found
            </div>
        )
    }

    if (!companyPageData || !salesIntelligenceStore?.exhibitorPageData) return null

    return (
        <div
            className={`salesInt-cards flex widgetContainer ${cssClass ?? ''} exhibitorView`}
            style={{ ...style, flexWrap: 'wrap' }}
        >
            <Card cssClass="dark" style={{ width: '100%', flex: '0 0 100%', color: 'white' }}>
                <CollapsibleMatrix
                    chartData={salesIntelligenceStore?.exhibitorPageData?.salesIntelligenceMatrix}
                    exhibitorName={salesIntelligenceStore?.company}
                    numResponses={exhibitorRecord?.surveyDataIds?.length}
                />
            </Card>
            <div
                className={`salesInt-cards flex-column widgetContainer ${cssClass ?? ''}`}
                style={{ width: 'auto', flex: 1 }}
            >
                <Card style={aiSummary.style}>
                    <AiSummaryWidget
                        appStore={salesIntelligenceStore.appStore}
                        surveyId={salesIntelligenceStore.surveyId}
                        filterString={exhibitorRecord?.surveyDataIds?.join('_')}
                        exhibitorName={salesIntelligenceStore.company}
                    />
                </Card>
                <Card style={aiSummary.style}>
                    {salesIntelligenceStore?.exhibitorPageData?.objectives?.error
                        ? (
                            <div style={{
                                height: '400px',
                                display: 'flex',
                                flexDirection: 'column',
                                justifyContent: 'center',
                                alignItems: 'center',
                                padding: '16px',
                                gap: '8px',
                            }}
                            >
                                <TargetIcon />
                                <div style={{ fontSize: '24px', fontWeight: '700' }}>Objectives question required</div>
                                <div style={{ width: '400px', textAlign: 'center' }}>
                                    To view insights please contact your survey
                                    administrator to configure an objectives question for this survey
                                </div>
                            </div>
                        )
                        : (
                            <SatisfactionOfObjectives
                                chartData={salesIntelligenceStore?.exhibitorPageData?.objectives}
                            />
                        )}
                </Card>
            </div>
            <div
                className={`salesInt-cards flex-column widgetContainer ${cssClass ?? ''}`}
                style={{ width: 'auto', flex: 1 }}
            >
                <div
                    className={`salesInt-cards flex widgetContainer ${cssClass ?? ''}`}
                    style={{ width: 'auto', flex: 1 }}
                >
                    <div
                        className={`salesInt-cards flex-column widgetContainer ${cssClass ?? ''}`}
                        style={{ width: 'auto', flex: 1 }}
                    >
                        <Card style={EEI.style} cssClass="dark">
                            <div>
                                <div className="widgetTitle">Exhibitor Execution index</div>
                                <hr className="titleHr" />
                                {salesIntelligenceStore?.exhibitorPageData?.EEI?.error
                                    ? (
                                        <CompoundError />
                                    )
                                    : (
                                        <CompoundKPI
                                            collapsible
                                            chartData={[{
                                                value: salesIntelligenceStore?.exhibitorPageData?.EEI?.value,
                                            }]}
                                            widget={{
                                                min: 1,
                                                max: 5,
                                                dependencies: salesIntelligenceStore?.exhibitorPageData?.EEI?.subData,
                                                kpiType: 'EEI',
                                            }}
                                            advancedSegments={[
                                                { min: 0, label: 'Sceptic', type: 'error' },
                                                { min: 3.0000000001, label: 'Optimist', type: 'success' },
                                            ]}
                                        />
                                    )}
                            </div>
                        </Card>
                        <Card cssClass="dark">
                            <div>
                                <div className="widgetTitle">Net promoter Score</div>
                                <hr className="titleHr" />
                                {salesIntelligenceStore?.exhibitorPageData?.NPS?.error
                                    ? (
                                        <CompoundError />
                                    )
                                    : (
                                        <CompoundKPI
                                            chartData={[{ value: salesIntelligenceStore?.exhibitorPageData?.NPS?.value }]}
                                            widget={{ dependencies: salesIntelligenceStore?.exhibitorPageData?.NPS?.subData, kpiType: 'NPS' }}
                                            scale={{ min: 0, max: 10 }}
                                            advancedSegments={[
                                                { min: 0, label: 'Detractor', type: 'error' },
                                                { min: 6.5, label: 'Passive', type: 'warning' },
                                                { min: 8.5, label: 'Promoter', type: 'success' },
                                            ]}
                                        />
                                    )}
                            </div>
                        </Card>
                    </div>
                    <div
                        className={`salesInt-cards flex-column widgetContainer ${cssClass ?? ''}`}
                        style={{ width: 'auto', flex: 1 }}
                    >
                        <Card style={ECI.style} cssClass="dark">
                            <div>
                                <div className="widgetTitle">Exhibitor Commitment index</div>
                                <hr className="titleHr" />
                                {salesIntelligenceStore?.exhibitorPageData?.ECI?.error
                                    ? (
                                        <CompoundError />
                                    )
                                    : (
                                        <CompoundKPI
                                            collapsible
                                            chartData={[{ value: salesIntelligenceStore?.exhibitorPageData?.ECI?.value }]}
                                            widget={{
                                                min: 1,
                                                max: 5,
                                                dependencies: salesIntelligenceStore?.exhibitorPageData?.ECI?.subData,
                                                kpiType: 'ECI',
                                            }}
                                            advancedSegments={[
                                                { min: 0, label: 'Lagging', type: 'error' },
                                                { min: 3.0000000001, label: 'Accomplished', type: 'success' },
                                            ]}
                                        />
                                    )}
                            </div>
                        </Card>
                        <Card cssClass="dark">
                            <div>
                                <div className="widgetTitle">Likelihood of return</div>
                                <hr className="titleHr" />
                                {salesIntelligenceStore?.exhibitorPageData?.LoR?.error
                                    ? (
                                        <CompoundError />
                                    )
                                    : (
                                        <CompoundKPI
                                            chartData={[{
                                                value: salesIntelligenceStore?.exhibitorPageData?.LoR?.value,
                                            }]}
                                            widget={{
                                                min: 1,
                                                max: 5,
                                                dependencies: salesIntelligenceStore?.exhibitorPageData?.LoR?.subData,
                                                kpiType: 'LoR',
                                            }}
                                            advancedSegments={[
                                                { min: 0, label: 'High risk', type: 'error' },
                                                { min: 1.5, label: 'At risk', type: 'warning' },
                                                { min: 2.5, label: 'On the fence', type: 'warning' },
                                                { min: 3.5, label: 'Likely', type: 'warning' },
                                                { min: 4.5, label: 'Committed', type: 'success' },
                                            ]}
                                        />
                                    )}
                            </div>
                        </Card>
                    </div>
                </div>
                <Card>
                    <GroupedVerbatimWidget
                        chartData={salesIntelligenceStore?.exhibitorPageData?.allTextQuestions}
                    />
                </Card>
            </div>
        </div>
    )
})

export default ExhibitorView
